import {
  ActionIcon,
  Box,
  Group,
  Stack,
  Text,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";
import { DatePickerInput, MonthPickerInput } from "@mantine/dates";
import { useState } from "react";

import {
  getDateRangeFromValue,
  getPresetValueItem,
  isTimeframeRange,
  setRangeByType,
} from "./utils";

import { IconCalendar } from "@/components/icons";
import Popover from "@/components/widgets/Popover/Popover";

import type { MantineStyleProps } from "@mantine/core";
import type { DatePickerInputProps } from "@mantine/dates";
import type { FC } from "react";
import type { PresetValue, PresetValueItem, TimeframePreset, TimeframeRange } from "./types";

type InputProps = Pick<DatePickerInputProps, "highlightToday" | "miw" | "placeholder" | "w">;

export type TimeframeInputProps = {
  defaultValue?: TimeframePreset | TimeframeRange;
  disabled?: boolean;
  hidePicker?: boolean;
  /** When true, renders the picker inline instead of opening a nested modal on mobile. */
  inlinePicker?: boolean;
  inputProps?: InputProps;
  label?: string;
  maxDate?: Date | string;
  minDate?: Date | string;
  onChange?: (value?: TimeframeRange) => void;
  presets?: Array<PresetValue | PresetValueItem>;
  type?: "date" | "month";
  valueFormat?: DatePickerInputProps["valueFormat"];
} & MantineStyleProps;

export const TimeframeInput: FC<TimeframeInputProps> = ({
  defaultValue,
  disabled = false,
  hidePicker = false,
  inlinePicker = false,
  inputProps = { highlightToday: true, miw: 208 },
  label,
  maxDate,
  minDate,
  onChange,
  presets = ["all", "ytd", "3y", "5y"],
  type = "date",
  valueFormat = "DD MMM YYYY",
  ...props
}) => {
  const [range, setRange] = useState<TimeframeRange | undefined>(
    isTimeframeRange(defaultValue)
      ? defaultValue
      : getDateRangeFromValue(getPresetValueItem(defaultValue)?.value)
  );
  const [currentValue, setValue] = useState(
    isTimeframeRange(defaultValue) ? undefined : defaultValue
  );
  const [popoverOpened, setPopoverOpened] = useState(false);

  const isMobile = useMatches({ base: true, md: false });
  const useMobilePopover = isMobile && !inlinePicker;
  const showPresets = (!isMobile || inlinePicker) && presets.length > 0;
  const timeframeRange = setRangeByType(type);
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const inputStyles = {
    label: {
      color: "var(--mantine-color-dimmed)",
    },
    placeholder: {
      color: isDark ? "var(--mantine-color-secondary-3)" : "var(--mantine-color-gray-6)",
    },
  };

  function handleTimeframeValue(value: PresetValue) {
    setValue(value);

    const range = getDateRangeFromValue(value);
    setRange(timeframeRange(range));
    onChange?.(timeframeRange(range));
  }

  function handleTimeframeRange(range: TimeframeRange) {
    setValue(undefined);
    setRange(timeframeRange(range));
    onChange?.(timeframeRange(range));
  }

  const presetsContent = showPresets ? (
    <Group gap="sm">
      {presets.map((preset, index) => {
        const item = getPresetValueItem(preset);
        const isActive = item?.value === currentValue;
        const variant = isActive ? "light" : "subtle";

        return item ? (
          <ActionIcon
            key={index}
            disabled={disabled}
            variant={variant}
            onClick={() => handleTimeframeValue(item.value)}
          >
            <Text size="xs" fw={isActive ? 600 : 500}>
              {item.label}
            </Text>
          </ActionIcon>
        ) : null;
      })}
    </Group>
  ) : null;

  const pickerContent = !hidePicker ? (
    <>
      {useMobilePopover ? (
        <Popover
          title={label}
          actionInPending={false}
          popoverOpened={popoverOpened}
          setPopoverOpened={setPopoverOpened}
          target={
            <Box
              component="div"
              role="button"
              tabIndex={0}
              onClick={() => setPopoverOpened(true)}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setPopoverOpened(true);
                }
              }}
              style={{
                all: "unset",
                cursor: "pointer",
                width: "100%",
              }}
            >
              <DatePickerInput
                clearable
                disabled={disabled}
                label={label}
                leftSection={<IconCalendar size={24} />}
                value={range}
                maxDate={maxDate}
                minDate={minDate}
                onChange={(value) => value && handleTimeframeRange(value as TimeframeRange)}
                type="range"
                valueFormat={valueFormat}
                styles={inputStyles}
                readOnly
                {...inputProps}
              />
            </Box>
          }
        >
          {type === "date" && (
            <DatePickerInput
              clearable
              disabled={disabled}
              label={label}
              leftSection={<IconCalendar size={24} />}
              value={range}
              maxDate={maxDate}
              minDate={minDate}
              onChange={(value) => value && handleTimeframeRange(value as TimeframeRange)}
              type="range"
              valueFormat={valueFormat}
              styles={inputStyles}
              {...inputProps}
            />
          )}
          {type === "month" && (
            <MonthPickerInput
              allowSingleDateInRange
              clearable
              disabled={disabled}
              label={label}
              leftSection={<IconCalendar size={24} />}
              value={range}
              maxDate={maxDate}
              minDate={minDate}
              onChange={(value) => value && handleTimeframeRange(value as TimeframeRange)}
              type="range"
              valueFormat={valueFormat}
              styles={inputStyles}
              {...inputProps}
            />
          )}
        </Popover>
      ) : (
        <>
          {type === "date" && (
            <DatePickerInput
              clearable
              disabled={disabled}
              label={label}
              leftSection={<IconCalendar size={24} />}
              value={range}
              maxDate={maxDate}
              minDate={minDate}
              onChange={(value) => value && handleTimeframeRange(value as TimeframeRange)}
              type="range"
              valueFormat={valueFormat}
              styles={inputStyles}
              {...inputProps}
            />
          )}
          {type === "month" && (
            <MonthPickerInput
              allowSingleDateInRange
              clearable
              disabled={disabled}
              label={label}
              leftSection={<IconCalendar size={24} />}
              value={range}
              maxDate={maxDate}
              minDate={minDate}
              onChange={(value) => value && handleTimeframeRange(value as TimeframeRange)}
              type="range"
              valueFormat={valueFormat}
              styles={inputStyles}
              {...inputProps}
            />
          )}
        </>
      )}
    </>
  ) : null;

  if (inlinePicker) {
    return (
      <Stack gap="md" {...props}>
        {pickerContent}
        {presetsContent}
      </Stack>
    );
  }

  return (
    <Group align="flex-end" gap="lg" {...props}>
      {pickerContent}
      {presetsContent}
    </Group>
  );
};
